import React, { useState } from 'react';
import { ClientEvent, clientEvents } from '../../data/clientEventsData';
import { fetchClientImages, GalleryImage } from '../../services/imageService';
import EventGalleryModal from '../shared/EventGalleryModal';
import { generateClientEventAltText } from '../../utils/imageUtils';
import LazyImage from '../ui/LazyImage';

const GalleryIndustrial: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState<ClientEvent | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [companyImages, setCompanyImages] = useState<GalleryImage[]>([]);
  const [loadingImages, setLoadingImages] = useState(false);

  const handleCompanyClick = async (company: ClientEvent) => {
    setSelectedCompany(company);
    setIsModalOpen(true);
    setLoadingImages(true);

    // Fetch images when company is selected
    try {
      const images = await fetchClientImages(company.id, company.company);
      setCompanyImages(images);
    } catch (error) {
      console.error('Error fetching images:', error);
      setCompanyImages([]);
    } finally {
      setLoadingImages(false);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
    setCompanyImages([]);
  };

  return (
    <section id="galeria" className="section-industrial">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>        
        {/* Company Grid */}
        <div className="gallery-grid">
          {clientEvents.map((company) => (
            <div
              key={company.id}
              className="gallery-company-card"
              onClick={() => handleCompanyClick(company)}
            >
              <div className="company-logo">
                {company.image ? (
                  <LazyImage
                    src={company.image}
                    alt={company.imageAlt || generateClientEventAltText(company.company, company.description)}
                    className="company-logo-image"
                    effect="blur"
                    threshold={50}
                  />
                ) : (
                  <span className="logo-icon">{company.logo}</span>
                )}
              </div>
              <div className="company-info">
                <h3 className="company-name">{company.company}</h3>
                <p className="company-event">{company.id}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Event Gallery Modal */}
        <EventGalleryModal
          isOpen={isModalOpen}
          selectedEvent={selectedCompany}
          images={companyImages}
          loadingImages={loadingImages}
          onClose={handleCloseModal}
        />
      </div>
    </section>
  );
};

// Add styles component
const styles = `
<style>
.gallery-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: calc(var(--industrial-unit) * 4);
  margin: calc(var(--industrial-unit) * 6) 0;
}

.gallery-company-card {
  background: var(--industrial-white);
  border-radius: calc(var(--industrial-border-radius) * 2);
  padding: 0;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  border: 2px solid transparent;
  min-height: 320px;
  display: flex;
  flex-direction: column;
}

.gallery-company-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  border-color: var(--industrial-primary);
}

.gallery-company-card:hover .company-logo {
  filter: grayscale(0%);
}

.gallery-company-card:hover .company-logo::after {
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.5) 50%, transparent 100%);
}

.company-logo {
  background: var(--industrial-white);
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  filter: grayscale(100%);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  flex: 1;
  border-radius: calc(var(--industrial-border-radius) * 2);
}

.gallery-company-card:hover .company-logo {
  filter: grayscale(0%);
}

.company-logo::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%);
  z-index: 1;
  pointer-events: none;
}

.logo-icon {
  font-size: 4rem;
  color: var(--industrial-dark);
  position: relative;
  z-index: 0;
}

.company-logo-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
}

.company-info {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  text-align: center;
  z-index: 2;
  padding: calc(var(--industrial-unit) * 2);
  background: transparent;
}

.company-name {
  font-family: var(--industrial-font-heading);
  font-size: 1.1rem;
  font-weight: 700;
  color: var(--industrial-white);
  margin-bottom: calc(var(--industrial-unit) * 0.5);
  text-transform: uppercase;
  letter-spacing: 1px;
  line-height: 1.3;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.8);
}

.company-event {
  font-family: var(--industrial-font-secondary);
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 0;
  font-weight: 500;
  line-height: 1.4;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.8);
}

.company-badges {
  display: flex;
  justify-content: center;
  gap: calc(var(--industrial-unit) * 1);
  margin-bottom: calc(var(--industrial-unit) * 3);
  flex-wrap: wrap;
}

.event-type-badge {
  font-family: var(--industrial-font-secondary);
  display: inline-block;
  padding: 4px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.event-type-badge.masivo {
  background: rgba(239, 68, 68, 0.1);
  color: #EF4444;
}

.event-type-badge.mediano {
  background: rgba(245, 158, 11, 0.1);
  color: #F59E0B;
}

.event-type-badge.corporativo {
  background: rgba(59, 130, 246, 0.1);
  color: #3B82F6;
}

@media (max-width: 768px) {
  .gallery-grid {
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: calc(var(--industrial-unit) * 3);
  }

  .gallery-company-card {
    min-height: 280px;
  }

  .logo-icon {
    font-size: 3rem;
  }

  .company-info {
    padding: calc(var(--industrial-unit) * 1.5);
  }

  .company-name {
    font-size: 1rem;
  }

  .company-event {
    font-size: 0.75rem;
  }

  .company-badges {
    gap: calc(var(--industrial-unit) * 0.5);
  }

  .event-type-badge {
    font-size: 0.65rem;
    padding: 3px 6px;
  }
}

@media (max-width: 480px) {
  .gallery-grid {
    grid-template-columns: 1fr;
    gap: calc(var(--industrial-unit) * 2);
  }

  .gallery-company-card {
    min-height: 250px;
  }

  .logo-icon {
    font-size: 2.5rem;
  }

  .company-info {
    padding: calc(var(--industrial-unit) * 1);
  }

  .company-name {
    font-size: 0.9rem;
  }

  .company-event {
    font-size: 0.7rem;
  }

  .company-badges {
    flex-direction: column;
    gap: calc(var(--industrial-unit) * 0.5);
  }
}
</style>
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.getElementById('gallery-styles');
  if (!styleElement) {
    const style = document.createElement('style');
    style.id = 'gallery-styles';
    style.innerHTML = styles.replace('<style>', '').replace('</style>', '');
    document.head.appendChild(style);
  }
}

export default GalleryIndustrial;
